/** @jsxImportSource theme-ui @jsxRuntime classic */

import { H } from '@hzdg/sectioning';
import { VisuallyHidden } from '@reach/visually-hidden';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Section,
  SocialCarouselCardProps,
  Text,
  TishmanThemeName,
  useBreakpointIndex,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import React, { useMemo, useState } from 'react';

import Picture from '../../../components/src/Picture';

type SocialSectionProps = {
  cards: SocialCarouselCardProps[] | null;
  title: string;
  copy: string;
  hashtag: string;
  theme: TishmanThemeName;
};

export function SocialSection({
  cards,
  title,
  copy,
  hashtag,
  theme,
}: SocialSectionProps): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useBreakpointIndex() < 2;

  const displayedCards = useMemo(() => {
    const perPage = isMobile ? 1 : 6; // Show 1 card per page on small screens, otherwise show 6
    return cards?.slice(currentIndex, currentIndex + perPage);
  }, [cards, currentIndex, isMobile]);

  return (
    <Section theme={theme}>
      <Container>
        <Grid columns={[1, '1fr 2fr']} gap={0} py={4}>
          <Flex
            sx={{
              flexDirection: 'column',
              justifyContent: 'center',
              pr: [0, 5],
            }}
          >
            <Box pt={3}>
              <H
                sx={{
                  variant: 'styles.h3',
                  fontSize: 6,
                  fontFamily: 'headingSecondary',
                  mb: 4,
                }}
              >
                {title}
              </H>
            </Box>
            <Box>
              <Text sx={{ fontSize: 3 }}>{copy}</Text>
            </Box>
            <Flex
              mt={4}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ fontWeight: 'bold', py: [4, 0] }}>{hashtag}</Box>
              {/* PREVIOUS & NEXT ARROW BUTTONS */}
              {cards && (
                <Flex>
                  {/* Previous */}
                  <Button
                    disabled={currentIndex === 0}
                    onClick={() =>
                      setCurrentIndex((prevIndex) => Math.max(prevIndex - 6, 0))
                    }
                    variant="icon"
                  >
                    <VisuallyHidden>Previous Item</VisuallyHidden>
                    <Icons.Arrow
                      aria-hidden
                      style={{
                        transition: 'transform 0.3s ease-in-out',
                        transform: `scale(${24 / 18}) rotate(180deg)`,
                      }}
                    />
                  </Button>
                  {/* Next */}
                  <Button
                    disabled={currentIndex >= cards?.length - 6}
                    onClick={() =>
                      setCurrentIndex((prevIndex) =>
                        Math.min(prevIndex + 6, cards?.length - 1),
                      )
                    }
                    variant="icon"
                  >
                    <VisuallyHidden>Next Item</VisuallyHidden>
                    <Icons.Arrow
                      aria-hidden
                      style={{
                        transition: 'transform 0.3s ease-in-out',
                        transform: `scale(${24 / 18})`,
                      }}
                    />
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
          {/* SOCIAL IMAGE */}
          <Box>
            <Grid gap={1} width={[128, null, 192]}>
              {displayedCards &&
                displayedCards.map((card) => (
                  <Picture
                    alt={card.caption}
                    imageSizes={[350]}
                    key={card.id}
                    src={card.image}
                  />
                ))}
            </Grid>
          </Box>
        </Grid>
      </Container>
    </Section>
  );
}
